// Create/Edit Practice Project Wizard Page - Redesigned to match CreateProject design
// Date: January 21, 2026

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Home, FolderOpen } from 'lucide-react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import {
  usePracticeProjectQuery,
  useCreatePracticeProjectMutation,
  useUpdatePracticeProjectMutation,
} from '../../api/practiceProjectsApi';
import { useLinkDatasetMutation, useCreateDatasetMutation as useCreateDatasetRepoMutation } from '../../api/createDatasetApi';
import paths from '../../routes';
import { Skeleton } from '../../components/ui/skeleton';
import { StepIndicator } from '../../components/CreateProject';
import { ProjectDetailsStep, ProjectAssetsStep, QAManagementStep } from './CreatePracticeProject';

export default function CreatePracticeProjectPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const isEditMode = !!projectId;

  const [currentStep, setCurrentStep] = useState(1);

  // Form data matching regular project structure
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    difficulty_level: 'beginner' as 'beginner' | 'intermediate' | 'advanced',
    tags: [] as string[],
    project_overview: '',
    learning_objectives: '',
    prerequisites: '',
    instructions: '', // For Step 2 readme content
    estimated_hours: '',
    notebook_template_s3_url: '',
    dataset_s3_urls: [] as string[],
    datasets: [] as any[], // For uploaded datasets
    selectedDatasetIds: [] as string[], // For selected existing datasets
    originalLinkedDatasetIds: [] as string[], // For change detection in edit mode
    additional_files_s3_urls: [] as string[],
    notebookFiles: [] as any[], // For uploading .py/.ipynb files
    max_attempts: 3,
    time_limit_hours: 0,
    passing_score: 70,
    qa_items: [] as any[],
    resource_profile_id: '',
    sandbox_time_duration: '01:00',
  });

  const steps = [
    {
      number: 1,
      title: 'Project Details',
      subtitle: 'Basic information and learning objectives',
    },
    {
      number: 2,
      title: 'Assets & Instructions',
      subtitle: 'Upload datasets and notebook content',
    },
    {
      number: 3,
      title: 'Q&A Module',
      subtitle: 'Add self-assessment questions',
    },
  ];

  // Fetch existing project in edit mode
  const { data: existingProject, isLoading: loadingProject } = usePracticeProjectQuery(
    projectId || '',
    { enabled: isEditMode }
  );

  // Mutations
  const createMutation = useCreatePracticeProjectMutation();
  const updateMutation = useUpdatePracticeProjectMutation();
  const linkDatasetMutation = useLinkDatasetMutation();
  const createDatasetRepoMutation = useCreateDatasetRepoMutation();

  // Load existing project data
  useEffect(() => {
    if (existingProject?.data) {
      const project = existingProject.data as any;
      
      console.log('[EditMode] Loading project data:', {
        project_id: project.id,
        resource_profile_id: project.resource_profile_id,
        has_questions: !!project.questions,
        questions_count: project.questions?.length || 0,
        has_qaItems: !!project.qaItems,
        qaItems_count: project.qaItems?.length || 0,
        has_datasets: !!project.datasets,
        datasets_count: project.datasets?.length || 0
      });
      
      console.log('[EditMode] Raw Q&A data:', {
        questions: project.questions,
        qaItems: project.qaItems
      });
      
      // Extract linked dataset IDs if available
      const linkedDatasetIds = project.datasets?.map((ds: any) => ds.id) || [];
      
      // Map existing additional files (notebooks/modules) to the format expected by the UI
      const existingNotebookFiles = (project.additional_files_s3_urls || []).map((key: string) => ({
        id: key,
        name: key.split('/').pop() || 'Unknown File',
        size: '',
        isExisting: true,
        url: key
      }));
      
      setFormData({
        title: project.title || '',
        description: project.description || '',
        difficulty_level: project.difficulty_level || 'beginner',
        tags: project.tags || [],
        project_overview: project.project_overview || '',
        learning_objectives: project.learning_objectives || '',
        prerequisites: project.prerequisites || '',
        instructions: project.instructions || '',
        estimated_hours: '',
        notebook_template_s3_url: project.notebook_template_s3_url || '',
        dataset_s3_urls: project.dataset_s3_url || [],
        datasets: project.datasets?.filter((ds: any) => ds.visibility === 'PROJECT') || [],
        selectedDatasetIds: linkedDatasetIds,
        originalLinkedDatasetIds: linkedDatasetIds,
        additional_files_s3_urls: project.additional_files_s3_urls || [],
        notebookFiles: existingNotebookFiles,
        max_attempts: 3,
        time_limit_hours: 0,
        passing_score: 70,
        qa_items: project.questions 
          ? project.questions.map((q: any) => ({
              question_text: q.question_text,
              variable_name: q.variable_name || '',
              check: q.check || {
                expected_output: null,
                check_expression: '',
                hint_text: '',
                solution_text: '',
                correct_message: 'Correct! Well done.',
                incorrect_message: 'Incorrect. Try again.',
              },
              order_index: q.order_index,
            }))
          : project.qaItems || [],
        resource_profile_id: String(project.resource_profile_id || ''),
        sandbox_time_duration: project.sandbox_time_duration || '01:00',
      });
      
      console.log('[EditMode] Form data set with:', {
        resource_profile_id: String(project.resource_profile_id || ''),
        qa_items_count: (project.questions?.length || project.qaItems?.length || 0)
      });
    }
  }, [existingProject]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Q&A Item Management Functions
  const addQAItem = () => {
    const newQA = {
      question_text: '',
      variable_name: '',
      check: {
        expected_output: null,
        check_expression: '',
        hint_text: '',
        solution_text: '',
        correct_message: '',
        incorrect_message: ''
      },
      order_index: formData.qa_items.length,
    };
    setFormData((prev) => ({
      ...prev,
      qa_items: [...prev.qa_items, newQA],
    }));
  };

  const updateQAItem = (index: number, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      qa_items: prev.qa_items.map((item: any, i: number) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const deleteQAItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      qa_items: prev.qa_items.filter((_: any, i: number) => i !== index).map((item: any, i: number) => ({
        ...item,
        order_index: i,
      })),
    }));
  };

  const reorderQAItem = (index: number, direction: 'up' | 'down') => {
    const newItems = [...formData.qa_items];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newItems.length) return;
    
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    
    // Update order_index
    const reorderedItems = newItems.map((item, i) => ({
      ...item,
      order_index: i,
    }));
    
    setFormData((prev) => ({ ...prev, qa_items: reorderedItems }));
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        if (!formData.title.trim()) {
          toast.error('Project title is required');
          return false;
        }
        if (!formData.description.trim()) {
          toast.error('Project description is required');
          return false;
        }
        if (!formData.resource_profile_id) {
          toast.error('Resource profile is required');
          return false;
        }
        if (!formData.sandbox_time_duration || !/^(\d{1,2}):([0-5]\d)$/.test(formData.sandbox_time_duration)) {
          toast.error('Please enter a valid sandbox duration (HH:MM format)');
          return false;
        }
        return true;
      case 2:
        // Assets are optional for practice projects
        return true;
      case 3:
        // Q&A items are optional for practice projects
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, steps.length));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    try {
      // Step 1: Create or update the practice project
      const { datasets, selectedDatasetIds, originalLinkedDatasetIds, notebookFiles, ...restFormData } = formData;

      // Synchronize additional_files_s3_urls with notebookFiles (only keep existing ones that are still present)
      const remainingExistingFiles = (notebookFiles || [])
        .filter((f: any) => f.isExisting)
        .map((f: any) => f.url);
      
      const updatedRestFormData = {
        ...restFormData,
        additional_files_s3_urls: remainingExistingFiles
      };

      // Check for NEW notebook files to upload
      const newNotebookFiles = (notebookFiles || []).filter((f: any) => f.file);
      const hasNewNotebookFiles = newNotebookFiles.length > 0;
      
      let practiceProjectId = projectId;

      if (hasNewNotebookFiles) {
        // Use FormData when uploading new notebook files
        const submitFormData = new FormData();
        
        // Append all text fields from updatedRestFormData
        Object.keys(updatedRestFormData).forEach((key) => {
          const value = updatedRestFormData[key as keyof typeof updatedRestFormData];
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              // For arrays in FormData, append each item separately
              if (value.length === 0) {
                // Send empty array as JSON string
                submitFormData.append(key, JSON.stringify([]));
              } else {
                value.forEach((item) => {
                  submitFormData.append(`${key}[]`, typeof item === 'object' ? JSON.stringify(item) : String(item));
                });
              }
            } else {
              submitFormData.append(key, String(value));
            }
          }
        });

        // Append estimated_hours and status
        submitFormData.append('estimated_hours', String(parseInt(formData.estimated_hours) || 0));
        submitFormData.append('status', 'draft');

        // Append new notebook files
        newNotebookFiles.forEach((fileData: any) => {
          if (fileData.file) {
            submitFormData.append('notebookFiles', fileData.file);
          }
        });

        if (isEditMode && projectId) {
          await updateMutation.mutateAsync({ id: projectId, payload: submitFormData });
          toast.success('Practice project updated successfully!');
        } else {
          const result: any = await createMutation.mutateAsync(submitFormData);
          practiceProjectId = result?.data?.id || result?.id;
          toast.success('Practice project created successfully!');
        }
      } else {
        // Use JSON payload when no NEW notebook files
        const payload = {
          ...updatedRestFormData,
          estimated_hours: parseInt(formData.estimated_hours) || 0,
          status: 'draft' as const,
        };

        if (isEditMode && projectId) {
          await updateMutation.mutateAsync({ id: projectId, payload });
          toast.success('Practice project updated successfully!');
        } else {
          const result: any = await createMutation.mutateAsync(payload);
          practiceProjectId = result?.data?.id || result?.id;
          toast.success('Practice project created successfully!');
        }
      }

      // Step 2: Handle dataset operations if we have a project ID
      if (practiceProjectId) {
        // Handle uploaded files - create datasets from them
        // Uses PROJECT visibility (not PRIVATE) to match Normal Projects behavior
        // This ensures uploaded files are hidden from "Select Dataset" tab
        const uploadedFiles = datasets?.filter((ds: any) => ds.file) || [];
        const uploadedDatasetIds: string[] = [];

        if (uploadedFiles.length > 0) {
          try {
            for (const fileData of uploadedFiles) {
              if (!fileData.file) continue;

              const datasetFormData = new FormData();
              datasetFormData.append('title', `${restFormData.title}-Dataset`);
              datasetFormData.append('datasetFile', fileData.file);
              datasetFormData.append('visibility', 'PROJECT'); // PROJECT visibility = hidden from Select Dataset tab
              datasetFormData.append('projectId', practiceProjectId); // Auto-links to practice project (backend handles both project types)

              const response: any = await createDatasetRepoMutation.mutateAsync(datasetFormData);
              if (response?.isSuccess && response?.data?.id) {
                uploadedDatasetIds.push(response.data.id);
              }
            }

            if (uploadedDatasetIds.length > 0) {
              toast.success(`${uploadedDatasetIds.length} file(s) uploaded as datasets`);
            }
          } catch (error) {
            console.error('[Upload Files Error]', error);
            toast.error('Failed to upload some files');
          }
        }

        // Step 3: Link all datasets (selected + uploaded) to practice project
        const allDatasetIds = [...(selectedDatasetIds || []), ...uploadedDatasetIds];
        const originalIds = originalLinkedDatasetIds || [];

        // Check if datasets have changed
        const hasChanged =
          allDatasetIds.length !== originalIds.length ||
          !allDatasetIds.every((id: string) => originalIds.includes(id));

        if (hasChanged) {
          try {
            await linkDatasetMutation.mutateAsync({
              projectId: practiceProjectId,
              datasetIds: allDatasetIds,
            });
            
            if (allDatasetIds.length > 0) {
              toast.success(`${allDatasetIds.length} dataset(s) updated successfully`);
            } else if (originalIds.length > 0) {
              toast.success('All datasets unlinked successfully');
            }
          } catch (error) {
            console.error('[Dataset Link Error]', error);
            toast.warning('Project saved but failed to update dataset links');
          }
        }
      }

      // Navigate to manage projects page
      navigate('/app/manage-projects?tab=practice-projects');
    } catch (error: any) {
      console.error('[Submit Error]', error);
      toast.error(error?.response?.data?.message || 'Failed to save practice project');
    }
  };

  if (loadingProject) {
    return (
      <div className="p-6">
        <Skeleton className="h-8 w-64 mb-4" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="p-6">
        <div className="mb-6">
          <div className="flex items-start p-4">
            <Breadcrumb className="bg-transparent border-none shadow-none">
              <BreadcrumbList className="bg-transparent">
                <BreadcrumbItem>
                  <BreadcrumbLink
                    onClick={() => navigate('/')}
                    className="cursor-pointer flex items-center"
                  >
                    <Home className="h-4 w-4" />
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink
                    onClick={() => navigate('/app/manage-projects')}
                    className="cursor-pointer flex items-center underline"
                  >
                    <FolderOpen className="h-4 w-4 mr-1" />
                    Project Management
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="bg-transparent">
                    {isEditMode ? 'Edit' : 'Create New'} Practice Project
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </div>

        <div className="flex items-center space-x-4 mb-12">
          <Button variant="ghost" size="sm" onClick={() => navigate('/app/manage-projects?tab=practice-projects')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-semibold text-gray-900">
            {isEditMode ? 'Edit' : 'Create New'} Practice Project
          </h1>
        </div>

        {/* Step Indicator */}
        <StepIndicator steps={steps} currentStep={currentStep} />

        {/* Form Content */}
        <div className="mb-8">
        <form onSubmit={(e) => e.preventDefault()}>
          {/* Step 1: Project Details */}
          {currentStep === 1 && (
            <ProjectDetailsStep
              formData={formData}
              handleInputChange={handleInputChange}
            />
          )}

          {/* Step 2: Assets & Instructions */}
          {currentStep === 2 && (
            <ProjectAssetsStep
              formData={formData}
              handleInputChange={handleInputChange}
            />
          )}

          {/* Step 3: Q&A Module */}
          {currentStep === 3 && (
            <QAManagementStep
              qaItems={formData.qa_items}
              addQAItem={addQAItem}
              updateQAItem={updateQAItem}
              deleteQAItem={deleteQAItem}
              reorderQAItem={reorderQAItem}
              projectId={projectId}
              projectName={formData.title}
            />
          )}
        </form>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <div>
            {currentStep > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                Back
              </Button>
            )}
          </div>
          <div className="flex space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/app/manage-projects?tab=practice-projects')}
            >
              Cancel
            </Button>
            {currentStep < steps.length ? (
              <Button
                type="button"
                onClick={handleNext}
                className="bg-bits-blue hover:bg-bits-blue-700"
              >
                Next
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={createMutation.isPending || updateMutation.isPending}
                className="bg-bits-blue hover:bg-bits-blue-700"
              >
                {createMutation.isPending || updateMutation.isPending
                  ? 'Saving...'
                  : isEditMode
                  ? 'Update Practice Project'
                  : 'Create Practice Project'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
